import { motion, AnimatePresence } from "framer-motion";
import { X, GitCompare, Plus, Trash2 } from "lucide-react";
import { formatMoney } from "../../utils/formatMoney";

export default function ComparisonBar({
  selectedPolicies = [],
  onRemove,
  onCompare,
  onClear,
  maxSelection = 3,
}) {
  if (selectedPolicies.length === 0) return null;

  const slots = Array.from({ length: maxSelection }, (_, index) => selectedPolicies[index] || null);
  const canCompare = selectedPolicies.length >= 2;
  const remaining = maxSelection - selectedPolicies.length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pr-16 sm:px-6 sm:pr-20 lg:px-8"
      >
        <div className="pointer-events-auto mx-auto max-w-5xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-4 sm:p-4">
            <div className="flex items-center justify-between gap-3 sm:w-36 sm:shrink-0 sm:flex-col sm:items-start sm:justify-center">
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
                  <GitCompare className="h-4 w-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Compare</p>
                  <p className="text-xs text-gray-500">
                    {selectedPolicies.length} of {maxSelection}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClear}
                className="inline-flex min-h-11 items-center gap-1 rounded-lg px-2 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 sm:min-h-0 sm:px-0"
                aria-label="Clear all selections"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clear
              </button>
            </div>

            <div className="grid min-w-0 flex-1 grid-cols-3 gap-2">
              {slots.map((policy, index) =>
                policy ? (
                  <div
                    key={policy.id}
                    className="relative min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-2.5 py-2"
                  >
                    <button
                      type="button"
                      onClick={() => onRemove(policy.id)}
                      className="absolute -right-1.5 -top-1.5 grid h-6 w-6 place-items-center rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-gray-200 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      aria-label={`Remove ${policy.policyName}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <p className="truncate text-xs font-semibold text-gray-900">
                      {policy.provider}
                    </p>
                    <p className="truncate text-[11px] text-gray-500">{policy.policyName}</p>
                    <p className="mt-1 text-sm font-bold text-emerald-700">
                      {formatMoney(policy.premiumMin)}
                    </p>
                  </div>
                ) : (
                  <div
                    key={`empty-${index}`}
                    className="flex min-h-[4.5rem] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white px-2 text-center text-gray-400"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    <span className="mt-1 text-[11px] font-medium">
                      {remaining > 0 ? "Add policy" : "Slot"}
                    </span>
                  </div>
                )
              )}
            </div>

            <button
              type="button"
              onClick={onCompare}
              disabled={!canCompare}
              className="inline-flex h-11 min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
            >
              <GitCompare className="h-4 w-4" aria-hidden="true" />
              {canCompare ? `Compare ${selectedPolicies.length}` : "Select 2+"}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
